<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue"
import { RouterLink, useRoute } from "vue-router"

import { LogoutButton } from "@/features/logout"

const tabs = [
  { to: "/feed", label: "Feed" },
  { to: "/sprints", label: "Sprints" },
  { to: "/sessions", label: "Sessions" },
  { to: "/journals", label: "Journals" },
  { to: "/topics", label: "Topics" },
  { to: "/factors", label: "Factors" },
  { to: "/map", label: "Map" },
  { to: "/practice-map", label: "Practice map" },
  { to: "/timeline", label: "Timeline" },
  { to: "/daily", label: "Daily" },
  { to: "/insights", label: "Insights" },
  { to: "/asanas", label: "Asanas" },
  { to: "/me", label: "Account" },
]

function scrollToShowActive(row: HTMLElement, keepIfShown: boolean): number {
  const active = row.querySelector<HTMLElement>("[aria-current='page']")
  if (!active) return row.scrollLeft
  if (active === row.querySelector("a")) return 0
  const right = active.offsetLeft + active.offsetWidth
  const shown = active.offsetLeft >= row.scrollLeft && right <= row.scrollLeft + row.clientWidth
  if (keepIfShown && shown) return row.scrollLeft
  const overflow = right - row.clientWidth
  if (overflow <= 0) return 0
  const starts = [...row.querySelectorAll<HTMLElement>("a")].map((a) => a.offsetLeft)
  return Math.min(starts.find((left) => left >= overflow) ?? active.offsetLeft, active.offsetLeft)
}

const route = useRoute()
const tabRow = ref<HTMLElement | null>(null)
const tail = ref(0)
async function showActive(keepIfShown: boolean) {
  await nextTick()
  const row = tabRow.value
  if (!row) return
  const target = scrollToShowActive(row, keepIfShown)
  tail.value = Math.max(0, target - (row.scrollWidth - tail.value - row.clientWidth))
  await nextTick()
  row.scrollLeft = target
}

watch(() => route.path, () => showActive(true), { immediate: true })

let rowWidth = 0
const observer =
  typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver(() => {
        const width = tabRow.value?.clientWidth ?? 0
        if (width !== rowWidth) {
          rowWidth = width
          showActive(false)
        }
      })
watch(tabRow, (el) => el && observer?.observe(el))
onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <nav class="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-2">
    <div ref="tabRow" class="relative flex min-w-0 items-center gap-1 overflow-x-auto">
      <span class="mr-3 shrink-0 font-bold whitespace-nowrap">🧘 Meditation</span>
      <RouterLink
        v-for="tab in tabs"
        :key="tab.to"
        :to="tab.to"
        class="shrink-0 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap text-gray-600 hover:bg-gray-100"
        active-class="bg-indigo-50 font-semibold text-indigo-700"
      >{{ tab.label }}</RouterLink>
      <span v-if="tail" aria-hidden="true" class="shrink-0" :style="{ width: `${tail}px` }" />
    </div>
    <LogoutButton class="shrink-0" />
  </nav>
</template>
